<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Города</div>

            <div class="content-header__actions">
                <router-link :to="{ name: 'AddCity' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--1-line-filters"></div>
        </div>

        <FilterPanel
            :columns="2"
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="city_name"
                v-model="citiesFilter.name"
                label="Название города"
            />

            <UIDatePicker
                id="created_at"
                v-model="citiesCreatedAtFilter"
                label="Дата добавления"
                :options="{
                    enableTime: false,
                    dateFormat: 'd.m.Y',
                    mode: 'range'
                }"
            />
        </FilterPanel>

        <UISearchResults
            :count="cityPaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isCitiesLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arCities"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
            >
                <template #created_at="{ row }">
                    {{ formatFromUTC(row.created_at) }}
                </template>

                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditCity', params: { city_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>
                        </template>
                    </UIDropDown>
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="cityPaginationMeta?.pagination.last_page ?? 1"
                :current-page="cityPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="cityPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, computed } from 'vue';

    import { CitiesRequestFilter } from '@/types/city.type.ts';

    import useCityRepository from '@/repositories/city/useCityRepository.ts';
    import { useCitiesFilterStore } from '@/stores/cities/cities-filter.ts';

    import { formatFromUTC, formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const { arCities, cityPaginationMeta, isCitiesLoading, getCities } = useCityRepository();
    const citiesFilterStore = useCitiesFilterStore();

    const citiesFilter = ref<CitiesRequestFilter>({ ...citiesFilterStore.$state });
    const headers = ref([
        { label: 'Название города', key: 'name', sortable: true },
        { label: 'Дата добавления', key: 'created_at', sortable: true },
    ]);

    const citiesCreatedAtFilter = computed<(string | undefined)[]>({
        get: () => {
            if (citiesFilter.value.created_at === undefined) {
                return [undefined, undefined];
            }

            return [
                citiesFilter.value.created_at[0].length > 0 ? citiesFilter.value.created_at[0] : undefined,
                citiesFilter.value.created_at[1].length > 0 ? citiesFilter.value.created_at[1] : undefined,
            ];
        },
        set: (dates: (string | undefined)[]) => {
            if (
                dates[0] !== undefined && dates[0].length > 0
                && dates[1] !== undefined && dates[1].length > 0
            ) {
                citiesFilter.value.created_at = [
                    dates[0],
                    dates[1],
                ];
            }
            else {
                citiesFilter.value.created_at = undefined;
            }
        },
    });

    const sortKey = computed<string | undefined>(() => {
        if (citiesFilter.value.sort) {
            return Object.keys(citiesFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (citiesFilter.value.sort) {
            return Object.values(citiesFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    function onSearch(): void {
        citiesFilter.value.page = 1;
        fetchCities();
    }

    function onReset(): void {
        citiesFilter.value.name = '';
        citiesFilter.value.created_at = undefined;
        citiesFilter.value.page = 1;
        fetchCities();
    }

    function changePage(page: number): void {
        citiesFilter.value.page = page;
        fetchCities();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        citiesFilter.value.sort = dir === undefined ? [{ name: 'asc' }] : [{ [key]: dir }];
        fetchCities();
    }

    function mapCitiesFilterToRequest(): CitiesRequestFilter {
        const requestFilter: CitiesRequestFilter = {
            page: citiesFilter.value.page,
            sort: citiesFilter.value.sort,
        };

        if (citiesFilter.value.name !== undefined && citiesFilter.value.name.length > 0) {
            requestFilter.name = citiesFilter.value.name;
        }

        if (
            citiesFilter.value.created_at !== undefined
        ) {
            requestFilter.created_at = [
                formatToUTC(citiesFilter.value.created_at[0], {
                    fromFormat: 'dd.LL.yyyy',
                    toFormat: 'yyyy-LL-dd',
                    withTime: false,
                }),
                formatToUTC(citiesFilter.value.created_at[1], {
                    fromFormat: 'dd.LL.yyyy',
                    toFormat: 'yyyy-LL-dd',
                    withTime: false,
                }),
            ];
        }

        return requestFilter;
    }

    function fetchCities(): void {
        citiesFilterStore.$patch({ ...citiesFilter.value });
        getCities(mapCitiesFilterToRequest());
    }

    onMounted(() => {
        fetchCities();
    });
</script>
